/**
 * Main App Component for TECDocChart Root-Level React Shell
 * 
 * Issue #68 - Root-Level React Router & Django URL Restructure
 * 
 * Provides tenant-aware initialization with React Router for healthcare
 * application with HIPAA compliance and progressive AngularJS migration.
 * 
 * Features:
 * - Root-level React Router replacing Django URL routing
 * - Automatic tenant context detection and configuration  
 * - Session-based authentication with Django backend
 * - Healthcare-specific error boundaries and logging
 * - Protected routes with employee context validation
 */

import { RouterProvider } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ConfigProvider } from 'antd';
import { LoadingProvider } from './contexts/LoadingContext';
import { store } from './store/store';
import { router } from './router';
import ErrorBoundary from './components/shared/ErrorBoundary';
import './App.css';

function App() {
  return (
    <Provider store={store}>
      {/*
        Modals sit above the legacy AngularJS chrome, which is why the stacking is raised at all
        (c68fb8a). It has to be raised through antd's zIndex token rather than by styling the
        modal directly: antd derives every popup layer -- Select, DatePicker, AutoComplete,
        Tooltip, Dropdown -- from this token, so an inline z-index on the modal lifts the modal
        and leaves the popups underneath it.

        That is what `styles: { mask: 9999, wrapper: 10000 }` did. Measured in Chrome, a Select
        opened inside a Modal put its menu at z-index 1150 under a wrapper at 10000: the menu was
        laid out at full size with every option present, painted beneath the modal, and
        elementFromPoint at each option returned `ant-modal-content`. Clicks landed on the modal
        and nothing threw, so the control read as disabled while inline fields kept working --
        reported as lab result dropdowns having no values.

        With the token the whole stack scales together: wrapper 10000, menu 10150, options
        clickable.
      */}
      {/*
        Placeholder and disabled colours (#286). antd ships colorTextPlaceholder and
        colorTextDisabled as aliases of the SAME token, colorTextQuaternary
        (rgba(0,0,0,0.25) -- 1.84:1 on white), so an empty Select and a disabled one
        are identical by construction and cannot drift apart without overriding both.

        These have to come through the theme token for the same reason zIndexPopupBase
        did. A Select's placeholder is .ant-select-selection-placeholder, a span --
        not a ::placeholder pseudo-element -- so no amount of CSS in forms.css reaches
        it, and antd's own .ant-input::placeholder outranks a bare input::placeholder
        rule anyway. The token is the only lever that covers both.

        Values are literals because antd derives colours from these numerically and
        cannot consume var(--x). They mirror --text-placeholder / --text-disabled /
        --background-disabled in styles/common/variables.css; change both together.
      */}
      <ConfigProvider
        theme={{
          token: {
            zIndexPopupBase: 10000,
            colorTextPlaceholder: '#616161', // --gray-700, 6.19:1 on white
            colorTextDisabled: '#9e9e9e', // --gray-500, intentionally low (WCAG 1.4.3 exempts)
            colorBgContainerDisabled: '#f5f5f5', // --gray-100
          },
        }}
      >
        <LoadingProvider>
          <ErrorBoundary>
            <RouterProvider router={router} />
          </ErrorBoundary>
        </LoadingProvider>
      </ConfigProvider>
    </Provider>
  );
}

export default App;
